import { useLoadScript } from "@react-google-maps/api";
import ToggleButtonComponent from "components/ToggleButton/ToggleButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";

import useAutocompleteService from "../../customHooks/usePlacesAutocompleteService";
import TaskLocationMap from "../../custom_modules/TasksNew/TaskLocationMap";
import {
  AddTaskLocationInfoContainer,
  AddTaskLocationSection,
} from "../../custom_modules/TasksNew/CreateTask/createTaskStyles";
import {
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from "../../styledComponents/buttons";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from "../../styledComponents/common";
import { checkHtml, checkUrl } from "../../utils/helper";
import { timezoneToCoords } from "../../utils/timeZoneCoordinates";
import CreateProjectDropdown from "../DropdownStyle/CreateProjectDropdown";
import ButtonTextLoader from "../Loaders/ButtonTextLoader";
import ComponentCircleLoader from "../Loaders/ComponentCircleLoader";
import MapsAutocompleteDropdown from "../MapsAutocompleteDropdown/MapsAutocompleteDropdown";

const mapLibraries = ["places"];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const AddOrgLocationModal = ({
  isOpen,
  toggle,
  organization_id,
  addOrgLocation,
  addOrgLocationLoading,
  createdOrgLocationInfo,
  selectedOrganization,
}) => {
  const { t } = useTranslation();

  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [radiusInput, setRadiusInput] = useState(100);
  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [100, 150, 200, 300];
  const [isCreated, setIsCreated] = useState(false);
  const [addressMarker, setAddressMarker] = useState(null);
  const [defaultLocationSetStatus, setDefaultLocationSetStatus] =
    useState(false);

  // map states
  const [map, setMap] = useState(null);

  // Get default center based on organization timezone
  const defaultMapCenter = useMemo(() => {
    if (selectedOrganization?.timezone) {
      const timezoneCoords = timezoneToCoords(selectedOrganization.timezone);
      if (timezoneCoords) {
        return { lat: timezoneCoords.lat, lng: timezoneCoords.lng };
      }
    }
    // Fallback to default center (Makkah)
    return { lat: 21.424090674479555, lng: 39.822141261515604 };
  }, [selectedOrganization?.timezone]);

  const { isLoaded, loadError } = useLoadScript({
    id: "org-location-map-script-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 5000 });

  const clearState = useCallback(() => {
    setLocationName("");
    setLocationAddress("");
    setRadiusInput(100);
    setSaveErrors({});
    setAddressMarker(null);
    setMap(null);
    setIsCreated(false);
    setDefaultLocationSetStatus(false);
  }, []);

  const handleToggle = useCallback(() => {
    clearState();
    toggle();
  }, [clearState, toggle]);

  useEffect(() => {
    if (isOpen) {
      setLocationName("");
      setLocationAddress("");
      setRadiusInput(100);
      setDefaultLocationSetStatus(false);
      setAddressMarker(null);
      setSaveErrors({});
      setIsCreated(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      createdOrgLocationInfo &&
      createdOrgLocationInfo.id &&
      isOpen &&
      isCreated
    ) {
      // Small delay to ensure state is updated before closing
      const timer = setTimeout(() => {
        handleToggle();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [createdOrgLocationInfo, isOpen, isCreated, handleToggle]);

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setLocationAddress(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {});
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };

  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setLocationAddress(results.results[0].formatted_address);
          } else {
            setLocationAddress(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {});
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };

  const handleSetAddressMarker = location => {
    if (location) {
      setAddressMarker(location);
      handleMapPanAndZoomToSingleLocation(location);
    }
  };

  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const onLocationNameChange = e => {
    const { value } = e.target;
    setLocationName(value);
    if (saveErrors && saveErrors["locationName"]) {
      const errors = { ...saveErrors };
      delete errors["locationName"];
      setSaveErrors(errors);
    }
  };

  const onLocationAddressChange = e => {
    const { value } = e.target;
    getPlacePredictions({ input: value });
    setLocationAddress(value);
    if (saveErrors && saveErrors["locationAddress"]) {
      const errors = { ...saveErrors };
      delete errors["locationAddress"];
      setSaveErrors(errors);
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;

    if (!locationName.trim()) {
      formIsValid = false;
      saveErrors["locationName"] = "Location name is required";
    } else if (checkUrl(locationName)) {
      formIsValid = false;
      saveErrors["locationName"] = "Location name can not contain url";
    } else if (checkHtml(locationName)) {
      formIsValid = false;
      saveErrors["locationName"] = "Invalid location name";
    }

    if (!locationAddress.trim()) {
      formIsValid = false;
      saveErrors["locationAddress"] = "Location address is required";
    } else if (checkUrl(locationAddress)) {
      formIsValid = false;
      saveErrors["locationAddress"] = "Address can not contain url";
    } else if (checkHtml(locationAddress)) {
      formIsValid = false;
      saveErrors["locationAddress"] = "Invalid address input";
    }

    if (!radiusInput) {
      formIsValid = false;
      saveErrors["radius"] = "Radius is required";
    } else if (radiusInput % 1 !== 0) {
      formIsValid = false;
      saveErrors["radius"] = "Fraction is not allowed for radius";
    } else if (radiusInput < 50) {
      formIsValid = false;
      saveErrors["radius"] = "Minimum value for radius is 50";
    }

    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const handleAddOrgLocation = () => {
    if (checkError()) {
      const payload = {
        name: locationName,
        address: locationAddress,
        radius: radiusInput,
        default: defaultLocationSetStatus,
      };

      if (addressMarker && addressMarker.lat) {
        payload.latitude =
          typeof addressMarker.lat === "function"
            ? addressMarker.lat()
            : addressMarker.lat;
        payload.longitude =
          typeof addressMarker.lng === "function"
            ? addressMarker.lng()
            : addressMarker.lng;
      }

      addOrgLocation({
        organization_id: organization_id,
        ...payload,
      });
      setIsCreated(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `950px` }}
    >
      <ModalBody style={{ padding: `0` }}>
        <AddTaskLocationSection>
          <AddTaskLocationInfoContainer>
            <CommonText name title fontSize="16px" margin="auto 0 10px 0">
              {t("add_location")}
            </CommonText>

            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t("location_name")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_location_name")}
                value={locationName}
                onChange={e => onLocationNameChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationName`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors["locationName"] &&
                saveErrors["locationName"] !== undefined && (
                  <FormAlert>{saveErrors["locationName"]}</FormAlert>
                )}
            </InputWithLabelSection>

            <InputWithLabelSection
              style={{ position: "relative" }}
              margin="20px 0 0"
            >
              <InputLabel>
                {t("location_address")} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t("type_location_address")}
                value={locationAddress}
                onChange={e => onLocationAddressChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationAddress`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors["locationAddress"] &&
                saveErrors["locationAddress"] !== undefined && (
                  <FormAlert>{saveErrors["locationAddress"]}</FormAlert>
                )}
              {!isPredictionsLoading &&
              placePredictions &&
              placePredictions.length &&
              locationAddress ? (
                <MapsAutocompleteDropdown
                  dropdownOptions={placePredictions}
                  handleSelect={handleGetGeocode}
                />
              ) : null}
            </InputWithLabelSection>

            <InputWithLabelSection margin="20px 0 0">
              <CommonGrid>
                <InputLabel>
                  {t("location_radius")} <RedStar>*</RedStar>
                </InputLabel>
              </CommonGrid>
              <CommonFlex justifyContent="space-between" gap="12px">
                {radiusOptions.map((option, i) => {
                  return (
                    <RadiusButton
                      key={option}
                      active={radiusInput === option}
                      onClick={() => {
                        if (radiusInput !== option) {
                          setRadiusInput(option);
                          if (saveErrors && saveErrors["radius"]) {
                            const errors = { ...saveErrors };
                            delete errors["radius"];
                            setSaveErrors(errors);
                          }
                        }
                      }}
                    >
                      {option} m
                    </RadiusButton>
                  );
                })}
              </CommonFlex>
              {saveErrors && saveErrors["radius"] && (
                <FormAlert>{saveErrors["radius"]}</FormAlert>
              )}
            </InputWithLabelSection>

            <InputWithLabelSection
              style={{ position: "relative" }}
              margin="20px 0 0"
            >
              <CommonFlex gap="12px">
                <CommonText>Set as default location</CommonText>
                <ToggleButtonComponent
                  active={defaultLocationSetStatus}
                  handleClick={() => setDefaultLocationSetStatus(prev => !prev)}
                />
              </CommonFlex>
            </InputWithLabelSection>

            <CommonGrid
              justifyContent="unset"
              padding="10px 0 0 0"
              margin="30px 0 0"
            >
              <WhiteButton onClick={() => handleToggle()}>
                {t("cancel")}
              </WhiteButton>
              <PrimaryButton
                disabled={!locationName || !locationAddress}
                onClick={() => handleAddOrgLocation()}
                borderRadius="32px"
              >
                {addOrgLocationLoading ? (
                  <ButtonTextLoader
                    loadingText={t("add_location")}
                    fontSize="13px"
                  />
                ) : (
                  t("add_location")
                )}
              </PrimaryButton>
            </CommonGrid>
          </AddTaskLocationInfoContainer>
          {loadError ? (
            <CommonText
              fontSize="14px"
              color="#fe5969"
              margin="20px"
              style={{ textAlign: "center" }}
            >
              Map cannot be loaded. Please check your API key configuration.
            </CommonText>
          ) : isLoaded ? (
            <TaskLocationMap
              setMap={setMap}
              singleAddressMarker={addressMarker}
              setAddressFromMap={handleSetAddressFromMap}
              radius={radiusInput}
              center={defaultMapCenter}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "85vh",
                borderLeft: "1px solid #e4e7eb",
              }}
            >
              <ComponentCircleLoader />
            </div>
          )}
        </AddTaskLocationSection>
      </ModalBody>
    </Modal>
  );
};

export default AddOrgLocationModal;
